/**
 * 🚀 INSTANCED EDGE RENDERING - Single Draw Call for All Edges
 * CAD-Level Performance: Native CATIA/SolidWorks seviyesi
 *
 * Key Features:
 * - Single GPU draw call for thousands of edges
 * - GPU-side transform updates (zero CPU overhead)
 * - Attribute instancing for color/width/visibility
 * - LOD support for distant edges
 * - Frustum culling on GPU
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { getCanvasResolutionVec2 } from '../FikoCanvasResolution';

/**
 * Edge curve type constants (matches STP importer values)
 * 0=UNKNOWN, 1=LINE, 2=CIRCLE, 3=ELLIPSE, 4=B_SPLINE, 5=TRIMMED
 */
export const EdgeCurveType = {
    UNKNOWN: 0,
    LINE: 1,
    CIRCLE: 2,
    ELLIPSE: 3,
    B_SPLINE: 4,
    TRIMMED: 5
} as const;

export type EdgeCurveTypeValue = typeof EdgeCurveType[keyof typeof EdgeCurveType];

/**
 * Edge metadata from STP topology (curveType, radius, radiusGroup)
 */
export interface EdgeMetadata {
    curveType: EdgeCurveTypeValue;
    radius: number;          // 0 for non-circular edges
    radiusGroup: number;     // -1 for ungrouped
}

/**
 * Edge filter configuration
 * All conditions are AND-combined. Omit a field to skip that filter.
 *
 * Usage:
 *   renderer.setEdgeFilter({ curveTypes: [2, 3] })           // Show only circles & ellipses
 *   renderer.setEdgeFilter({ curveTypes: [1] })              // Show only lines
 *   renderer.setEdgeFilter({ minRadius: 5, maxRadius: 20 })  // Show by radius range
 *   renderer.setEdgeFilter({ curveTypes: [2], minRadius: 3 }) // Circles with R>=3
 *   renderer.clearEdgeFilter()                                // Show all
 */
export interface EdgeFilterConfig {
    /** Which curveType values to show. If omitted, all types pass. */
    curveTypes?: EdgeCurveTypeValue[];
    /** Minimum radius (inclusive). Only edges with radius >= minRadius pass. */
    minRadius?: number;
    /** Maximum radius (inclusive). Only edges with radius <= maxRadius pass. */
    maxRadius?: number;
    /** Specific radiusGroup IDs to show. If omitted, all groups pass. */
    radiusGroups?: number[];
    /** If true, invert the filter (hide matching edges instead of showing them). */
    invert?: boolean;
}

/**
 * Edge instance data structure
 */
interface EdgeInstance {
    id: string;
    positions: Float32Array;  // [x1,y1,z1, x2,y2,z2]
    color: THREE.Color;
    width: number;
    visible: boolean;
    highlighted: boolean;
    selected: boolean;
    /** Edge metadata for filtering (curveType, radius, radiusGroup) */
    metadata: EdgeMetadata;
    /** User-set visibility (independent of filter). Both must be true to render. */
    userVisible: boolean;
}

/**
 * Instanced edge rendering configuration
 */
interface InstancedEdgeConfig {
    maxEdges: number;
    defaultColor: THREE.Color;
    hoverColor: THREE.Color;
    selectedColor: THREE.Color;
    defaultWidth: number;
    hoverWidth: number;
    selectedWidth: number;
    enableLOD: boolean;
    lodDistances: number[];
}

/**
 * 🎯 INSTANCED EDGE RENDERER
 * Renders all edges with a single draw call
 */
export class FikoInstancedEdgeRenderer {
    private renderer: THREE.WebGLRenderer;
    private gl: WebGL2RenderingContext | null = null;

    // Configuration
    private config: InstancedEdgeConfig;

    // Edge storage
    private edges: Map<string, EdgeInstance> = new Map();
    private edgeCount: number = 0;

    // 🚀 PERFORMANS OPT: Reusable buffers to eliminate allocations
    private static readonly _edgePosBuffer = new Float32Array(6);
    private static readonly _tempColor = new THREE.Color();
    private readonly _sizeBuffer = new THREE.Vector2();

    // 🚀 PERFORMANS OPT: Pre-allocated frustum culling objects
    private readonly _frustum = new THREE.Frustum();
    private readonly _projScreenMatrix = new THREE.Matrix4();
    private readonly _tempBox = new THREE.Box3();
    private readonly _tempVec3A = new THREE.Vector3();
    private readonly _tempVec3B = new THREE.Vector3();

    // 🚀 PERFORMANS OPT: Dirty flag to skip unnecessary buffer uploads
    private _edgesDirty = true;
    private _lastCameraPosition = new THREE.Vector3();
    private _lastCameraQuaternion = new THREE.Quaternion();

    // 🚀 PERFORMANS OPT: Distance-based LOD thresholds
    private static readonly LOD_FULL_DETAIL = 50;
    private static readonly LOD_HALF = 200;
    private static readonly LOD_QUARTER = 500;

    // GPU Buffers
    private positionBuffer: THREE.InstancedBufferAttribute | null = null;
    private colorBuffer: THREE.InstancedBufferAttribute | null = null;
    private widthBuffer: THREE.InstancedBufferAttribute | null = null;
    private visibilityBuffer: THREE.InstancedBufferAttribute | null = null;

    // Instanced mesh for rendering
    private instancedLineMesh: THREE.InstancedMesh | null = null;
    private edgeGeometry: THREE.BufferGeometry | null = null;

    // Line2 based rendering (fat lines)
    private lineGeometry: LineGeometry | null = null;
    private lineMaterial: LineMaterial | null = null;
    private line2Mesh: Line2 | null = null;

    // Custom shader for GPU-side instancing
    private edgeShaderMaterial: THREE.ShaderMaterial | null = null;

    // Performance metrics
    private metrics = {
        drawCalls: 0,
        totalEdges: 0,
        visibleEdges: 0,
        culledEdges: 0,
        lodSkippedEdges: 0,
        updateTimeMs: 0,
        renderTimeMs: 0
    };

    // Dirty flag
    private needsUpdate: boolean = false;
    private needsFullRebuild: boolean = false;

    // 🚀 PERFORMANS OPT: Dirty edges for partial update
    private dirtyEdges: Set<string> = new Set();
    private edgeIndexMap: Map<string, number> = new Map();

    // Pre-allocated arrays for batch updates
    private positionArray: Float32Array;
    private colorArray: Float32Array;
    private widthArray: Float32Array;
    private visibilityArray: Float32Array;

    // 🔍 Edge type filter
    private activeFilter: EdgeFilterConfig | null = null;
    // 🚀 PERFORMANS OPT: Pre-computed Set for O(1) curveType/radiusGroup lookup
    private _filterCurveTypeSet: Set<number> | null = null;
    private _filterRadiusGroupSet: Set<number> | null = null;

    constructor(renderer: THREE.WebGLRenderer, config?: Partial<InstancedEdgeConfig>) {
        this.renderer = renderer;
        this.gl = renderer.getContext() as WebGL2RenderingContext;

        this.config = {
            maxEdges: config?.maxEdges ?? 100000,
            defaultColor: config?.defaultColor ?? new THREE.Color(0x000000),
            hoverColor: config?.hoverColor ?? new THREE.Color(0x00ffff),
            selectedColor: config?.selectedColor ?? new THREE.Color(0xffff00),
            defaultWidth: config?.defaultWidth ?? 1,
            hoverWidth: config?.hoverWidth ?? 3,
            selectedWidth: config?.selectedWidth ?? 2,
            enableLOD: config?.enableLOD ?? true,
            lodDistances: config?.lodDistances ?? [100, 500, 1000]
        };

        // Pre-allocate arrays
        this.positionArray = new Float32Array(this.config.maxEdges * 6);  // 2 points * 3 coords
        this.colorArray = new Float32Array(this.config.maxEdges * 4);     // RGBA
        this.widthArray = new Float32Array(this.config.maxEdges);
        this.visibilityArray = new Float32Array(this.config.maxEdges);

        this.initializeShaders();
        this.initializeGeometry();
    }

    /**
     * 🚀 PERFORMANS OPT: Mark edge data as dirty to trigger buffer re-upload
     */
    markDirty(): void {
        this._edgesDirty = true;
    }

    /**
     * Initialize custom shaders for GPU-side edge rendering
     */
    private initializeShaders(): void {
        const vertexShader = `
            precision highp float;

            // Per-vertex attributes (unit line segment)
            attribute vec3 position;

            // Per-instance attributes
            attribute vec3 instanceStart;
            attribute vec3 instanceEnd;
            attribute vec4 instanceColor;
            attribute float instanceWidth;
            attribute float instanceVisible;

            // Uniforms
            uniform mat4 modelViewMatrix;
            uniform mat4 projectionMatrix;
            uniform vec2 resolution;

            // Varyings
            varying vec4 vColor;
            varying float vVisible;

            void main() {
                vColor = instanceColor;
                vVisible = instanceVisible;

                // Skip invisible edges
                if (instanceVisible < 0.5) {
                    gl_Position = vec4(0.0, 0.0, -2.0, 1.0); // Behind camera
                    return;
                }

                // Interpolate between start and end based on position.x (0 or 1)
                vec3 worldPos = mix(instanceStart, instanceEnd, position.x);

                // Transform to clip space
                vec4 clipPos = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);

                // Calculate screen-space direction for line width
                vec4 startClip = projectionMatrix * modelViewMatrix * vec4(instanceStart, 1.0);
                vec4 endClip = projectionMatrix * modelViewMatrix * vec4(instanceEnd, 1.0);

                vec2 startScreen = startClip.xy / startClip.w;
                vec2 endScreen = endClip.xy / endClip.w;

                vec2 dir = normalize(endScreen - startScreen);
                vec2 normal = vec2(-dir.y, dir.x);

                // Offset by line width (position.y is -1 or 1)
                float halfWidth = instanceWidth * 0.5 / resolution.y;
                clipPos.xy += normal * position.y * halfWidth * clipPos.w;

                gl_Position = clipPos;
            }
        `;

        const fragmentShader = `
            precision highp float;

            varying vec4 vColor;
            varying float vVisible;

            void main() {
                if (vVisible < 0.5) discard;
                gl_FragColor = vColor;
            }
        `;

        this.edgeShaderMaterial = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                resolution: { value: getCanvasResolutionVec2(this.renderer) }
            },
            transparent: true,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide
        });
    }

    /**
     * Initialize base geometry for instancing
     */
    private initializeGeometry(): void {
        // Create a unit line segment (0,0,0) to (1,0,0) with width
        // Each instance will transform this to actual edge positions
        const positions = new Float32Array([
            // Two triangles forming a quad
            0, -1, 0,  // Start, bottom
            1, -1, 0,  // End, bottom
            0,  1, 0,  // Start, top
            1,  1, 0,  // End, top
        ]);

        const indices = new Uint16Array([
            0, 1, 2,  // First triangle
            2, 1, 3   // Second triangle
        ]);

        this.edgeGeometry = new THREE.BufferGeometry();
        this.edgeGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.edgeGeometry.setIndex(new THREE.BufferAttribute(indices, 1));

        // Create instanced attributes
        this.positionBuffer = new THREE.InstancedBufferAttribute(
            new Float32Array(this.config.maxEdges * 3), 3
        );
        this.edgeGeometry.setAttribute('instanceStart', this.positionBuffer);

        const endBuffer = new THREE.InstancedBufferAttribute(
            new Float32Array(this.config.maxEdges * 3), 3
        );
        this.edgeGeometry.setAttribute('instanceEnd', endBuffer);

        this.colorBuffer = new THREE.InstancedBufferAttribute(
            new Float32Array(this.config.maxEdges * 4), 4
        );
        this.edgeGeometry.setAttribute('instanceColor', this.colorBuffer);

        this.widthBuffer = new THREE.InstancedBufferAttribute(
            new Float32Array(this.config.maxEdges), 1
        );
        this.edgeGeometry.setAttribute('instanceWidth', this.widthBuffer);

        this.visibilityBuffer = new THREE.InstancedBufferAttribute(
            new Float32Array(this.config.maxEdges), 1
        );
        this.edgeGeometry.setAttribute('instanceVisible', this.visibilityBuffer);

        // Create instanced mesh
        if (this.edgeShaderMaterial) {
            this.instancedLineMesh = new THREE.InstancedMesh(
                this.edgeGeometry,
                this.edgeShaderMaterial,
                this.config.maxEdges
            );
            this.instancedLineMesh.count = 0;
            this.instancedLineMesh.frustumCulled = false; // We handle culling ourselves
        }
    }

    /**
     * Add edge to renderer
     * @param metadata Optional edge metadata for curve-type filtering
     */
    addEdge(
        id: string,
        start: THREE.Vector3,
        end: THREE.Vector3,
        color?: THREE.Color,
        width?: number,
        metadata?: Partial<EdgeMetadata>
    ): void {
        if (this.edgeCount >= this.config.maxEdges) {
            return;
        }

        // 🚀 PERFORMANS OPT: Use static buffer, then copy to avoid per-edge allocation
        FikoInstancedEdgeRenderer._edgePosBuffer[0] = start.x;
        FikoInstancedEdgeRenderer._edgePosBuffer[1] = start.y;
        FikoInstancedEdgeRenderer._edgePosBuffer[2] = start.z;
        FikoInstancedEdgeRenderer._edgePosBuffer[3] = end.x;
        FikoInstancedEdgeRenderer._edgePosBuffer[4] = end.y;
        FikoInstancedEdgeRenderer._edgePosBuffer[5] = end.z;

        // 🚀 PERFORMANS OPT: Direct color copy without clone
        FikoInstancedEdgeRenderer._tempColor.copy(color ?? this.config.defaultColor);

        const edgeMeta: EdgeMetadata = {
            curveType: metadata?.curveType ?? EdgeCurveType.UNKNOWN,
            radius: metadata?.radius ?? 0,
            radiusGroup: metadata?.radiusGroup ?? -1
        };

        const edge: EdgeInstance = {
            id,
            positions: new Float32Array(FikoInstancedEdgeRenderer._edgePosBuffer),
            color: FikoInstancedEdgeRenderer._tempColor.clone(),
            width: width ?? this.config.defaultWidth,
            visible: true,
            highlighted: false,
            selected: false,
            metadata: edgeMeta,
            userVisible: true
        };

        // Apply active filter to new edge
        if (this.activeFilter) {
            edge.visible = this._edgePassesFilter(edge) && edge.userVisible;
        }

        this.edges.set(id, edge);
        this.edgeCount++;
        this.needsUpdate = true;
        this._edgesDirty = true;
    }

    /**
     * Add multiple edges at once (batch operation)
     * @param edges Array of edge data, optionally with metadata for filtering
     */
    addEdgesBatch(
        edges: Array<{
            id: string;
            start: THREE.Vector3;
            end: THREE.Vector3;
            color?: THREE.Color;
            metadata?: Partial<EdgeMetadata>;
        }>
    ): void {
        const startTime = performance.now();
        const hasFilter = this.activeFilter !== null;

        for (const edge of edges) {
            if (this.edgeCount >= this.config.maxEdges) break;

            // 🚀 PERFORMANS OPT: Use static buffer, then copy to avoid per-edge allocation
            FikoInstancedEdgeRenderer._edgePosBuffer[0] = edge.start.x;
            FikoInstancedEdgeRenderer._edgePosBuffer[1] = edge.start.y;
            FikoInstancedEdgeRenderer._edgePosBuffer[2] = edge.start.z;
            FikoInstancedEdgeRenderer._edgePosBuffer[3] = edge.end.x;
            FikoInstancedEdgeRenderer._edgePosBuffer[4] = edge.end.y;
            FikoInstancedEdgeRenderer._edgePosBuffer[5] = edge.end.z;

            // 🚀 PERFORMANS OPT: Direct color copy without clone
            FikoInstancedEdgeRenderer._tempColor.copy(edge.color ?? this.config.defaultColor);

            const edgeMeta: EdgeMetadata = {
                curveType: edge.metadata?.curveType ?? EdgeCurveType.UNKNOWN,
                radius: edge.metadata?.radius ?? 0,
                radiusGroup: edge.metadata?.radiusGroup ?? -1
            };

            const instance: EdgeInstance = {
                id: edge.id,
                positions: new Float32Array(FikoInstancedEdgeRenderer._edgePosBuffer),
                color: FikoInstancedEdgeRenderer._tempColor.clone(),
                width: this.config.defaultWidth,
                visible: true,
                highlighted: false,
                selected: false,
                metadata: edgeMeta,
                userVisible: true
            };

            // Apply active filter to new edge
            if (hasFilter) {
                instance.visible = this._edgePassesFilter(instance);
            }

            this.edges.set(edge.id, instance);
            this.edgeCount++;
        }

        this.needsFullRebuild = true;
        this._edgesDirty = true;
        this.metrics.updateTimeMs = performance.now() - startTime;
    }

    /**
     * Remove edge from renderer
     */
    removeEdge(id: string): void {
        if (this.edges.delete(id)) {
            this.edgeCount--;
            this.needsFullRebuild = true;
            this._edgesDirty = true;
        }
    }

    /**
     * Clear all edges
     */
    clearAllEdges(): void {
        this.edges.clear();
        this.edgeCount = 0;
        if (this.instancedLineMesh) {
            this.instancedLineMesh.count = 0;
        }
        this.needsUpdate = false;
        this.needsFullRebuild = false;
        this._edgesDirty = true;
    }

    /**
     * Set edge highlight state (hover)
     * 🚀 PERFORMANS OPT: Mark edge as dirty for partial update
     */
    setEdgeHighlight(id: string, highlighted: boolean): void {
        const edge = this.edges.get(id);
        if (edge) {
            edge.highlighted = highlighted;
            // 🚀 PERFORMANS OPT: Direct color copy without clone allocation
            edge.color.copy(highlighted ? this.config.hoverColor : this.config.defaultColor);
            edge.width = highlighted ? this.config.hoverWidth : this.config.defaultWidth;
            this.dirtyEdges.add(id);
            this.needsUpdate = true;
            this._edgesDirty = true;
        }
    }

    /**
     * Set edge selection state
     * 🚀 PERFORMANS OPT: Mark edge as dirty for partial update
     */
    setEdgeSelected(id: string, selected: boolean): void {
        const edge = this.edges.get(id);
        if (edge) {
            edge.selected = selected;
            if (selected) {
                // 🚀 PERFORMANS OPT: Direct color copy without clone allocation
                edge.color.copy(this.config.selectedColor);
                edge.width = this.config.selectedWidth;
            } else if (!edge.highlighted) {
                // 🚀 PERFORMANS OPT: Direct color copy without clone allocation
                edge.color.copy(this.config.defaultColor);
                edge.width = this.config.defaultWidth;
            }
            this.dirtyEdges.add(id);
            this.needsUpdate = true;
            this._edgesDirty = true;
        }
    }

    /**
     * Set edge visibility (user-level)
     * 🚀 PERFORMANS OPT: Mark edge as dirty for partial update
     * Note: actual visibility = userVisible AND filter pass
     */
    setEdgeVisible(id: string, visible: boolean): void {
        const edge = this.edges.get(id);
        if (edge) {
            edge.userVisible = visible;
            // Combine user visibility with filter
            edge.visible = visible && (this.activeFilter ? this._edgePassesFilter(edge) : true);
            this.dirtyEdges.add(id);
            this.needsUpdate = true;
            this._edgesDirty = true;
        }
    }

    // ═══════════════════════════════════════════════════════════════
    //  🔍 EDGE TYPE FILTERING - Filter edges by curveType / radius
    // ═══════════════════════════════════════════════════════════════

    /**
     * 🔍 Set edge filter - show/hide edges based on curve type and radius
     * CPU-side filtering: sets visibility per-edge, GPU shader discards invisible edges.
     * This is optimal because:
     *   1. No shader recompilation needed
     *   2. Visibility attribute already exists and is GPU-uploaded
     *   3. Vertex shader already discards invisible edges (gl_Position behind camera)
     *   4. Single pass over all edges, O(n) where n = total edges
     *
     * @param filter Filter configuration. All conditions are AND-combined.
     *
     * Examples:
     *   setEdgeFilter({ curveTypes: [EdgeCurveType.CIRCLE] })           // Only circles
     *   setEdgeFilter({ curveTypes: [EdgeCurveType.LINE] })             // Only straight lines
     *   setEdgeFilter({ minRadius: 5, maxRadius: 20 })                  // Radius range
     *   setEdgeFilter({ curveTypes: [EdgeCurveType.CIRCLE], minRadius: 3 })  // Circles R>=3
     *   setEdgeFilter({ curveTypes: [EdgeCurveType.CIRCLE], invert: true })  // Hide circles
     */
    setEdgeFilter(filter: EdgeFilterConfig): void {
        this.activeFilter = filter;

        // 🚀 PERFORMANS OPT: Pre-compute Sets for O(1) lookup in hot loop
        this._filterCurveTypeSet = filter.curveTypes
            ? new Set(filter.curveTypes)
            : null;
        this._filterRadiusGroupSet = filter.radiusGroups
            ? new Set(filter.radiusGroups)
            : null;

        // Apply filter to all existing edges
        this._applyFilterToAllEdges();
    }

    /**
     * 🔍 Clear edge filter - show all edges (restore user visibility)
     */
    clearEdgeFilter(): void {
        this.activeFilter = null;
        this._filterCurveTypeSet = null;
        this._filterRadiusGroupSet = null;

        // Restore all edges to their userVisible state
        for (const edge of this.edges.values()) {
            edge.visible = edge.userVisible;
        }

        this.needsFullRebuild = true;
        this._edgesDirty = true;
    }

    /**
     * 🔍 Get the currently active edge filter (null if none)
     */
    getActiveFilter(): EdgeFilterConfig | null {
        return this.activeFilter;
    }

    /**
     * 🔍 Get metadata for a specific edge
     */
    getEdgeMetadata(id: string): EdgeMetadata | null {
        const edge = this.edges.get(id);
        return edge ? edge.metadata : null;
    }

    /**
     * 🔍 Set metadata for a specific edge (e.g. after late topology resolution)
     */
    setEdgeMetadata(id: string, metadata: Partial<EdgeMetadata>): void {
        const edge = this.edges.get(id);
        if (edge) {
            if (metadata.curveType !== undefined) edge.metadata.curveType = metadata.curveType;
            if (metadata.radius !== undefined) edge.metadata.radius = metadata.radius;
            if (metadata.radiusGroup !== undefined) edge.metadata.radiusGroup = metadata.radiusGroup;

            // Re-evaluate filter if active
            if (this.activeFilter) {
                const newVisible = this._edgePassesFilter(edge) && edge.userVisible;
                if (newVisible !== edge.visible) {
                    edge.visible = newVisible;
                    this.dirtyEdges.add(id);
                    this.needsUpdate = true;
                    this._edgesDirty = true;
                }
            }
        }
    }

    /**
     * 🔍 Get edge statistics by curve type
     * Returns a breakdown of how many edges exist per curveType
     */
    getEdgeTypeStats(): Map<EdgeCurveTypeValue, { total: number; visible: number; radius?: { min: number; max: number; avg: number } }> {
        const stats = new Map<EdgeCurveTypeValue, { total: number; visible: number; radiusSum: number; radiusMin: number; radiusMax: number; radiusCount: number }>();

        for (const edge of this.edges.values()) {
            const ct = edge.metadata.curveType;
            let entry = stats.get(ct);
            if (!entry) {
                entry = { total: 0, visible: 0, radiusSum: 0, radiusMin: Infinity, radiusMax: -Infinity, radiusCount: 0 };
                stats.set(ct, entry);
            }
            entry.total++;
            if (edge.visible) entry.visible++;
            if (edge.metadata.radius > 0) {
                entry.radiusSum += edge.metadata.radius;
                entry.radiusMin = Math.min(entry.radiusMin, edge.metadata.radius);
                entry.radiusMax = Math.max(entry.radiusMax, edge.metadata.radius);
                entry.radiusCount++;
            }
        }

        // Convert to output format
        const result = new Map<EdgeCurveTypeValue, { total: number; visible: number; radius?: { min: number; max: number; avg: number } }>();
        for (const [ct, entry] of stats) {
            const out: { total: number; visible: number; radius?: { min: number; max: number; avg: number } } = {
                total: entry.total,
                visible: entry.visible
            };
            if (entry.radiusCount > 0) {
                out.radius = {
                    min: entry.radiusMin,
                    max: entry.radiusMax,
                    avg: entry.radiusSum / entry.radiusCount
                };
            }
            result.set(ct as EdgeCurveTypeValue, out);
        }
        return result;
    }

    /**
     * 🔍 Get all unique radius values present in edges
     */
    getUniqueRadii(): number[] {
        const radii = new Set<number>();
        for (const edge of this.edges.values()) {
            if (edge.metadata.radius > 0) {
                // Round to 3 decimal places to group near-identical radii
                radii.add(Math.round(edge.metadata.radius * 1000) / 1000);
            }
        }
        return Array.from(radii).sort((a, b) => a - b);
    }

    /**
     * 🔍 Get all unique radius group IDs
     */
    getRadiusGroups(): number[] {
        const groups = new Set<number>();
        for (const edge of this.edges.values()) {
            if (edge.metadata.radiusGroup >= 0) {
                groups.add(edge.metadata.radiusGroup);
            }
        }
        return Array.from(groups).sort((a, b) => a - b);
    }

    /**
     * 🔍 Get edge IDs that match a filter (without applying it)
     * Useful for selection or highlighting edges by type
     */
    getEdgeIdsByFilter(filter: EdgeFilterConfig): string[] {
        const oldCurveSet = this._filterCurveTypeSet;
        const oldRadiusSet = this._filterRadiusGroupSet;

        // Temporarily set filter sets for _edgePassesFilter
        this._filterCurveTypeSet = filter.curveTypes ? new Set(filter.curveTypes) : null;
        this._filterRadiusGroupSet = filter.radiusGroups ? new Set(filter.radiusGroups) : null;

        const result: string[] = [];
        for (const edge of this.edges.values()) {
            const passes = this._edgePassesFilterWith(edge, filter);
            if (passes) result.push(edge.id);
        }

        // Restore
        this._filterCurveTypeSet = oldCurveSet;
        this._filterRadiusGroupSet = oldRadiusSet;
        return result;
    }

    /**
     * 🚀 PERFORMANS OPT: Check if a single edge passes the active filter
     * Uses pre-computed Sets for O(1) lookup
     */
    private _edgePassesFilter(edge: EdgeInstance): boolean {
        const filter = this.activeFilter;
        if (!filter) return true;
        return this._edgePassesFilterWith(edge, filter);
    }

    /**
     * 🚀 PERFORMANS OPT: Check edge against a specific filter config
     */
    private _edgePassesFilterWith(edge: EdgeInstance, filter: EdgeFilterConfig): boolean {
        const meta = edge.metadata;
        let passes = true;

        // CurveType filter
        if (this._filterCurveTypeSet && !this._filterCurveTypeSet.has(meta.curveType)) {
            passes = false;
        }

        // Radius range filter
        if (passes && filter.minRadius !== undefined && meta.radius < filter.minRadius) {
            passes = false;
        }
        if (passes && filter.maxRadius !== undefined && meta.radius > filter.maxRadius) {
            passes = false;
        }

        // RadiusGroup filter
        if (passes && this._filterRadiusGroupSet && meta.radiusGroup >= 0 && !this._filterRadiusGroupSet.has(meta.radiusGroup)) {
            passes = false;
        }

        // Invert
        if (filter.invert) {
            passes = !passes;
        }

        return passes;
    }

    /**
     * 🚀 PERFORMANS OPT: Apply current filter to all edges in one pass
     * Triggers full rebuild for GPU buffer re-upload
     */
    private _applyFilterToAllEdges(): void {
        for (const edge of this.edges.values()) {
            edge.visible = this._edgePassesFilter(edge) && edge.userVisible;
        }
        this.needsFullRebuild = true;
        this._edgesDirty = true;
    }

    /**
     * 🚀 Update GPU buffers (SINGLE draw call preparation)
     * 🚀 PERFORMANS OPT: Partial update for dirty edges only
     * 🚀 PERFORMANS OPT: Skip buffer upload if nothing changed (_edgesDirty)
     */
    updateBuffers(camera?: THREE.Camera): void {
        // 🚀 PERFORMANS OPT: Skip entirely if nothing changed
        if (!this._edgesDirty && !this.needsUpdate && !this.needsFullRebuild && this.dirtyEdges.size === 0) return;

        const startTime = performance.now();

        const startAttr = this.edgeGeometry?.getAttribute('instanceStart') as THREE.InstancedBufferAttribute;
        const endAttr = this.edgeGeometry?.getAttribute('instanceEnd') as THREE.InstancedBufferAttribute;
        const colorAttr = this.edgeGeometry?.getAttribute('instanceColor') as THREE.InstancedBufferAttribute;
        const widthAttr = this.edgeGeometry?.getAttribute('instanceWidth') as THREE.InstancedBufferAttribute;
        const visibleAttr = this.edgeGeometry?.getAttribute('instanceVisible') as THREE.InstancedBufferAttribute;

        if (!startAttr || !endAttr || !colorAttr || !widthAttr || !visibleAttr) return;

        // 🚀 PERFORMANS OPT: Build frustum from camera for culling
        let hasFrustum = false;
        if (camera) {
            this._projScreenMatrix.multiplyMatrices(
                camera.projectionMatrix,
                camera.matrixWorldInverse
            );
            this._frustum.setFromProjectionMatrix(this._projScreenMatrix);
            hasFrustum = true;
        }

        let visibleCount = 0;

        // 🚀 PERFORMANS OPT: Partial update - only dirty edges
        if (!this.needsFullRebuild && this.dirtyEdges.size > 0 && this.dirtyEdges.size < this.edges.size * 0.3) {
            // Partial update (less than 30% dirty)
            let minIdx = Infinity;
            let maxIdx = -1;

            for (const edgeId of this.dirtyEdges) {
                const idx = this.edgeIndexMap.get(edgeId);
                const edge = this.edges.get(edgeId);
                if (idx === undefined || !edge) continue;

                minIdx = Math.min(minIdx, idx);
                maxIdx = Math.max(maxIdx, idx);

                // 🚀 PERFORMANS OPT: Frustum cull individual dirty edges
                let edgeVisible = edge.visible;
                if (edgeVisible && hasFrustum) {
                    edgeVisible = this._isEdgeInFrustum(edge);
                }

                // Update single edge
                startAttr.setXYZ(idx, edge.positions[0], edge.positions[1], edge.positions[2]);
                endAttr.setXYZ(idx, edge.positions[3], edge.positions[4], edge.positions[5]);
                colorAttr.setXYZW(idx, edge.color.r, edge.color.g, edge.color.b, 1.0);
                widthAttr.setX(idx, edge.width);
                visibleAttr.setX(idx, edgeVisible ? 1.0 : 0.0);
            }

            // Set update range for partial GPU upload
            if (minIdx !== Infinity && maxIdx !== -1) {
                const rangeStart = minIdx * 3;
                const rangeCount = (maxIdx - minIdx + 1) * 3;
                // Use addUpdateRange for THREE.js r152+ compatibility
                startAttr.clearUpdateRanges();
                startAttr.addUpdateRange(rangeStart, rangeCount);
                endAttr.clearUpdateRanges();
                endAttr.addUpdateRange(rangeStart, rangeCount);
                colorAttr.clearUpdateRanges();
                colorAttr.addUpdateRange(minIdx * 4, (maxIdx - minIdx + 1) * 4);
                widthAttr.clearUpdateRanges();
                widthAttr.addUpdateRange(minIdx, maxIdx - minIdx + 1);
                visibleAttr.clearUpdateRanges();
                visibleAttr.addUpdateRange(minIdx, maxIdx - minIdx + 1);
            }

            // Count visible edges
            for (const edge of this.edges.values()) {
                if (edge.visible) visibleCount++;
            }

            this.dirtyEdges.clear();
        } else {
            // Full rebuild with frustum culling + distance LOD
            let idx = 0;
            let edgeOrdinal = 0;
            this.edgeIndexMap.clear();

            // 🚀 PERFORMANS OPT: Get camera position once for distance LOD
            const cameraPos = camera ? camera.position : null;

            for (const [edgeId, edge] of this.edges) {
                if (idx >= this.config.maxEdges) break;
                edgeOrdinal++;

                // 🚀 PERFORMANS OPT: Frustum culling - skip edges outside view
                if (hasFrustum && edge.visible && !this._isEdgeInFrustum(edge)) {
                    continue;
                }

                // 🚀 PERFORMANS OPT: Distance-based LOD - skip edges based on distance
                if (cameraPos && edge.visible && this.config.enableLOD) {
                    // Use edge midpoint for distance calculation (reuse temp vec)
                    this._tempVec3A.set(
                        (edge.positions[0] + edge.positions[3]) * 0.5,
                        (edge.positions[1] + edge.positions[4]) * 0.5,
                        (edge.positions[2] + edge.positions[5]) * 0.5
                    );
                    const distSq = this._tempVec3A.distanceToSquared(cameraPos);

                    // Distance thresholds (squared to avoid sqrt)
                    const LOD_FULL_SQ = FikoInstancedEdgeRenderer.LOD_FULL_DETAIL * FikoInstancedEdgeRenderer.LOD_FULL_DETAIL;
                    const LOD_HALF_SQ = FikoInstancedEdgeRenderer.LOD_HALF * FikoInstancedEdgeRenderer.LOD_HALF;
                    const LOD_QUARTER_SQ = FikoInstancedEdgeRenderer.LOD_QUARTER * FikoInstancedEdgeRenderer.LOD_QUARTER;

                    if (distSq > LOD_QUARTER_SQ) {
                        // > 500 units: skip entirely (too far to see edges)
                        continue;
                    } else if (distSq > LOD_HALF_SQ) {
                        // 200-500 units: render every 4th edge
                        if ((edgeOrdinal & 3) !== 0) continue;
                    } else if (distSq > LOD_FULL_SQ) {
                        // 50-200 units: render every 2nd edge
                        if ((edgeOrdinal & 1) !== 0) continue;
                    }
                    // < 50 units: full detail - render all
                }

                // Track edge index for partial updates
                this.edgeIndexMap.set(edgeId, idx);

                // Start position
                startAttr.setXYZ(idx, edge.positions[0], edge.positions[1], edge.positions[2]);

                // End position
                endAttr.setXYZ(idx, edge.positions[3], edge.positions[4], edge.positions[5]);

                // Color (RGBA)
                colorAttr.setXYZW(idx, edge.color.r, edge.color.g, edge.color.b, 1.0);

                // Width
                widthAttr.setX(idx, edge.width);

                // Visibility
                visibleAttr.setX(idx, edge.visible ? 1.0 : 0.0);

                if (edge.visible) visibleCount++;
                idx++;
            }

            // Reset update range for full upload (THREE.js r152+)
            startAttr.clearUpdateRanges();
            endAttr.clearUpdateRanges();
            colorAttr.clearUpdateRanges();
            widthAttr.clearUpdateRanges();
            visibleAttr.clearUpdateRanges();

            // Update instance count
            if (this.instancedLineMesh) {
                this.instancedLineMesh.count = idx;
            }

            this.metrics.totalEdges = idx;
            this.dirtyEdges.clear();
        }

        // Mark attributes for GPU upload
        startAttr.needsUpdate = true;
        endAttr.needsUpdate = true;
        colorAttr.needsUpdate = true;
        widthAttr.needsUpdate = true;
        visibleAttr.needsUpdate = true;

        this.metrics.visibleEdges = visibleCount;
        this.metrics.updateTimeMs = performance.now() - startTime;

        this.needsUpdate = false;
        this.needsFullRebuild = false;
        this._edgesDirty = false;
    }

    /**
     * 🚀 PERFORMANS OPT: Check if an edge's bounding box intersects the camera frustum
     * Uses pre-allocated _tempBox, _tempVec3A, _tempVec3B to avoid allocations
     */
    private _isEdgeInFrustum(edge: EdgeInstance): boolean {
        this._tempVec3A.set(edge.positions[0], edge.positions[1], edge.positions[2]);
        this._tempVec3B.set(edge.positions[3], edge.positions[4], edge.positions[5]);

        // Build tight AABB from start/end points
        this._tempBox.min.x = Math.min(this._tempVec3A.x, this._tempVec3B.x);
        this._tempBox.min.y = Math.min(this._tempVec3A.y, this._tempVec3B.y);
        this._tempBox.min.z = Math.min(this._tempVec3A.z, this._tempVec3B.z);
        this._tempBox.max.x = Math.max(this._tempVec3A.x, this._tempVec3B.x);
        this._tempBox.max.y = Math.max(this._tempVec3A.y, this._tempVec3B.y);
        this._tempBox.max.z = Math.max(this._tempVec3A.z, this._tempVec3B.z);

        return this._frustum.intersectsBox(this._tempBox);
    }

    /**
     * 🎯 Get the renderable mesh (add to scene)
     * Pass camera to enable frustum culling and distance LOD
     */
    getMesh(camera?: THREE.Camera): THREE.InstancedMesh | null {
        this.updateBuffers(camera);
        return this.instancedLineMesh;
    }

    /**
     * Update shader uniforms (call before render)
     * 🚀 PERFORMANS OPT: Reuse size buffer instead of allocating
     */
    updateUniforms(camera: THREE.Camera): void {
        if (this.edgeShaderMaterial) {
            this.renderer.getSize(this._sizeBuffer);
            this.edgeShaderMaterial.uniforms['resolution'].value.copy(this._sizeBuffer);
        }
    }

    /**
     * Get performance metrics
     */
    getMetrics(): {
        drawCalls: number;
        totalEdges: number;
        visibleEdges: number;
        culledEdges: number;
        lodSkippedEdges: number;
        updateTimeMs: number;
        filteredEdges: number;
        hasActiveFilter: boolean;
    } {
        // Count filtered (hidden by filter) edges
        let filteredCount = 0;
        if (this.activeFilter) {
            for (const edge of this.edges.values()) {
                if (edge.userVisible && !edge.visible) filteredCount++;
            }
        }
        return {
            ...this.metrics,
            drawCalls: 1, // ALWAYS 1 - instanced rendering
            filteredEdges: filteredCount,
            hasActiveFilter: this.activeFilter !== null
        };
    }

    /**
     * Get all edge IDs
     */
    getEdgeIds(): string[] {
        return Array.from(this.edges.keys());
    }

    /**
     * Check if edge exists
     */
    hasEdge(id: string): boolean {
        return this.edges.has(id);
    }

    /**
     * Get edge count
     */
    getEdgeCount(): number {
        return this.edgeCount;
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        this.edges.clear();
        this.edgeCount = 0;

        if (this.edgeGeometry) {
            this.edgeGeometry.dispose();
        }

        if (this.edgeShaderMaterial) {
            this.edgeShaderMaterial.dispose();
        }

        if (this.instancedLineMesh) {
            this.instancedLineMesh.dispose();
        }

        if (this.lineGeometry) {
            this.lineGeometry.dispose();
        }

        if (this.lineMaterial) {
            this.lineMaterial.dispose();
        }
    }
}

/**
 * 🎯 Helper: Create instanced edge renderer from LineSegments
 * 🚀 PERFORMANS OPT: Reuse two Vector3 instances instead of allocating per edge
 * 🔧 FIX: Apply world matrix to vertex positions for correct curved edge rendering
 */
export function createInstancedEdgesFromLineSegments(
    renderer: THREE.WebGLRenderer,
    lineSegments: THREE.LineSegments
): FikoInstancedEdgeRenderer {
    const instancedRenderer = new FikoInstancedEdgeRenderer(renderer);

    const geometry = lineSegments.geometry;
    const positions = geometry.getAttribute('position');

    if (!positions) return instancedRenderer;

    const edges: Array<{ id: string; start: THREE.Vector3; end: THREE.Vector3 }> = [];

    // 🚀 PERFORMANS OPT: Pre-allocate vectors, clone only when pushing
    const _start = new THREE.Vector3();
    const _end = new THREE.Vector3();

    // 🔧 FIX: Apply world matrix transform to edge positions
    // Without this, edges stay in local space and curved tessellated edges
    // may appear at wrong positions
    const hasTransform = !lineSegments.matrixWorld.equals(new THREE.Matrix4());
    const worldMatrix = hasTransform ? lineSegments.matrixWorld : null;

    // Extract edge pairs
    for (let i = 0; i < positions.count; i += 2) {
        _start.set(positions.getX(i), positions.getY(i), positions.getZ(i));
        _end.set(positions.getX(i + 1), positions.getY(i + 1), positions.getZ(i + 1));

        // Apply world transform if present
        if (worldMatrix) {
            _start.applyMatrix4(worldMatrix);
            _end.applyMatrix4(worldMatrix);
        }

        edges.push({
            id: `edge_${i / 2}`,
            start: _start.clone(),
            end: _end.clone()
        });
    }

    instancedRenderer.addEdgesBatch(edges);

    return instancedRenderer;
}

/**
 * 🎯 Helper: Create instanced edge renderer from EdgesGeometry
 * 🔧 FIX: Use lower threshold angle (1 degree) to preserve tessellated curved edges.
 * THREE.EdgesGeometry filters edges where adjacent face normals differ by less than
 * thresholdAngle - this causes tessellated curves (circles, arcs) to disappear when
 * the angle between adjacent triangulation facets is small.
 */
export function createInstancedEdgesFromMesh(
    renderer: THREE.WebGLRenderer,
    mesh: THREE.Mesh,
    thresholdAngle: number = 1
): FikoInstancedEdgeRenderer {
    const edgesGeometry = new THREE.EdgesGeometry(mesh.geometry, thresholdAngle);
    const tempLineSegments = new THREE.LineSegments(edgesGeometry);

    // 🔧 FIX: Copy matrixWorld AND update it so createInstancedEdgesFromLineSegments
    // can apply the transform to vertex positions
    tempLineSegments.applyMatrix4(mesh.matrixWorld);

    const instancedRenderer = createInstancedEdgesFromLineSegments(renderer, tempLineSegments);

    // Cleanup temp - dispose both geometry and the auto-created default material
    edgesGeometry.dispose();
    if (tempLineSegments.material) {
        if (Array.isArray(tempLineSegments.material)) {
            tempLineSegments.material.forEach(m => m.dispose());
        } else {
            tempLineSegments.material.dispose();
        }
    }

    return instancedRenderer;
}

/**
 * 🎯 Helper: Create instanced edge renderer from pre-extracted edge geometry
 * This is designed for edge geometry produced by FikoImporter.STP's extractEdgesFromMesh()
 * which already contains tessellated curved edges as consecutive vertex pairs.
 *
 * Unlike createInstancedEdgesFromMesh(), this does NOT re-extract edges from mesh faces -
 * it directly reads the vertex pairs from the provided geometry, preserving all
 * tessellated curve segments (circles, arcs, splines).
 *
 * @param renderer WebGL renderer
 * @param edgeGeometry Pre-extracted edge geometry with position attribute (vertex pairs)
 * @param worldMatrix Optional world matrix to transform positions
 * @param color Optional edge color
 */
export function createInstancedEdgesFromEdgeGeometry(
    renderer: THREE.WebGLRenderer,
    edgeGeometry: THREE.BufferGeometry,
    worldMatrix?: THREE.Matrix4,
    color?: THREE.Color
): FikoInstancedEdgeRenderer {
    const instancedRenderer = new FikoInstancedEdgeRenderer(renderer);

    const positions = edgeGeometry.getAttribute('position');
    if (!positions) return instancedRenderer;

    const edges: Array<{ id: string; start: THREE.Vector3; end: THREE.Vector3; color?: THREE.Color; metadata?: Partial<EdgeMetadata> }> = [];

    // 🚀 PERFORMANS OPT: Pre-allocate vectors, clone only when pushing
    const _start = new THREE.Vector3();
    const _end = new THREE.Vector3();

    // Read edge group attribute if present (from STP importer)
    // grup attribute: [startVertexIdx, vertexCount, edgeId] per vertex
    const grupAttr = edgeGeometry.getAttribute('grup');

    // 🔍 Read curve metadata attributes from STP importer
    // curveType: 0=UNKNOWN, 1=LINE, 2=CIRCLE, 3=ELLIPSE, 4=B_SPLINE, 5=TRIMMED
    const curveTypeAttr = edgeGeometry.getAttribute('curveType') as THREE.BufferAttribute | undefined;
    const radiusAttr = edgeGeometry.getAttribute('radius') as THREE.BufferAttribute | undefined;
    const radiusGroupAttr = edgeGeometry.getAttribute('radiusGroup') as THREE.BufferAttribute | undefined;

    const hasMetadata = !!(curveTypeAttr || radiusAttr || radiusGroupAttr);

    // Extract all vertex pairs as individual instances
    // Each pair of consecutive vertices (i, i+1) forms one line segment instance
    for (let i = 0; i < positions.count; i += 2) {
        _start.set(positions.getX(i), positions.getY(i), positions.getZ(i));
        _end.set(positions.getX(i + 1), positions.getY(i + 1), positions.getZ(i + 1));

        // Apply world transform if present
        if (worldMatrix) {
            _start.applyMatrix4(worldMatrix);
            _end.applyMatrix4(worldMatrix);
        }

        // Use edge group ID from STP importer if available, otherwise sequential
        const edgeId = grupAttr
            ? `edge_g${grupAttr.getZ(i)}_s${i / 2}`
            : `edge_${i / 2}`;

        // 🔍 Extract metadata from BufferAttributes
        let metadata: Partial<EdgeMetadata> | undefined;
        if (hasMetadata) {
            metadata = {
                curveType: curveTypeAttr ? (curveTypeAttr.getX(i) as EdgeCurveTypeValue) : EdgeCurveType.UNKNOWN,
                radius: radiusAttr ? radiusAttr.getX(i) : 0,
                radiusGroup: radiusGroupAttr ? radiusGroupAttr.getX(i) : -1
            };
        }

        edges.push({
            id: edgeId,
            start: _start.clone(),
            end: _end.clone(),
            color,
            metadata
        });
    }

    instancedRenderer.addEdgesBatch(edges);

    return instancedRenderer;
}
